import { z } from 'zod';
import {
  SKILL_BODY_MAX_BYTES,
  SKILL_DESCRIPTION_MAX_LENGTH,
  SKILL_NAME_MAX_LENGTH,
  SKILL_SLUG_PATTERN,
} from './skills.constants';

export const SkillSourceSchema = z.enum(['PLATFORM', 'CUSTOMER']);
export type SkillSource = z.infer<typeof SkillSourceSchema>;

export const SkillStatusSchema = z.enum(['DRAFT', 'PUBLISHED']);
export type SkillStatus = z.infer<typeof SkillStatusSchema>;

export const SkillAuditActionSchema = z.enum([
  'CREATE',
  'UPDATE_DRAFT',
  'PUBLISH',
  'ROLLBACK',
  'DELETE',
  'RESTORE',
  'PLATFORM_SYNC',
]);
export type SkillAuditAction = z.infer<typeof SkillAuditActionSchema>;

function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4;
      i += 1;
    } else bytes += 3;
  }
  return bytes;
}

const isWithinByteLimit = (value: string, maxBytes: number) =>
  utf8ByteLength(value) <= maxBytes;

export const SkillSlugSchema = z
  .string()
  .min(3)
  .max(64)
  .regex(SKILL_SLUG_PATTERN, {
    error:
      'Slug must be lowercase alphanumeric with hyphens, no leading/trailing hyphen.',
  });

export const SkillBodySchema = z
  .string()
  .min(1)
  .refine((value) => isWithinByteLimit(value, SKILL_BODY_MAX_BYTES), {
    message: `Body must be at most ${SKILL_BODY_MAX_BYTES} bytes (UTF-8).`,
  })
  .refine((value) => !value.includes(String.fromCharCode(0)), {
    error: 'Body must not contain null bytes.',
  });

export const SkillFrontmatterSchema = z.strictObject({
  name: z.string().min(1).max(SKILL_NAME_MAX_LENGTH),
  description: z.string().min(1).max(SKILL_DESCRIPTION_MAX_LENGTH),
});

export type SkillFrontmatter = z.infer<typeof SkillFrontmatterSchema>;

export const SkillVersionSchema = z.object({
  versionId: z.string(),
  version: z.int().nonnegative(),
  status: SkillStatusSchema,
  contentHash: z.string(),
  body: SkillBodySchema,
  frontmatter: SkillFrontmatterSchema,
  authorId: z.string().nullable(),
  authorType: z.string(),
  publishedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type SkillVersionDto = z.infer<typeof SkillVersionSchema>;

export const SkillSchema = z.object({
  skillId: z.string(),
  tenantId: z.string(),
  applicationId: z.string().nullable(),
  agentId: z.string(),
  source: SkillSourceSchema,
  slug: SkillSlugSchema,
  bundleId: z.string().nullable(),
  mountPrefix: z.string(),
  publishedVersionId: z.string().nullable(),
  publishedVersion: SkillVersionSchema.nullable(),
  draftVersion: SkillVersionSchema.nullable(),
  deletedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type SkillDto = z.infer<typeof SkillSchema>;

export const SkillSummarySchema = SkillSchema.omit({
  publishedVersion: true,
  draftVersion: true,
}).extend({
  name: z.string(),
  description: z.string(),
  hasDraft: z.boolean(),
  publishedAt: z.iso.datetime().nullable(),
});
export type SkillSummaryDto = z.infer<typeof SkillSummarySchema>;

export const SkillAuditEntrySchema = z.object({
  auditId: z.string(),
  skillId: z.string(),
  versionId: z.string().nullable(),
  action: SkillAuditActionSchema,
  actorId: z.string().nullable(),
  actorType: z.string(),
  metadata: z.unknown().nullable(),
  createdAt: z.iso.datetime(),
});
export type SkillAuditEntryDto = z.infer<typeof SkillAuditEntrySchema>;
